/**
 * Premade Story Detail Page
 *
 * Shows detailed information about a premade storybook including
 * product images, description, pricing, and CTA to personalise.
 */

import 'flickity/dist/flickity.min.css'

import { GetStaticPaths, GetStaticPropsContext, NextPage } from 'next'
import { Layout, withLayoutProps, GlobalProps } from 'slices/Layout'

import { BaseSeo } from '@/components/seo/BaseSeo'
import {
  storefrontPremadeStoriesList,
  storefrontPremadeStoriesRetrieve,
} from '@/shared/api/generated/storefront-sessions/storefront-sessions'
import { PremadeStoryDetail } from '@/shared/api/models'
import { EMPTY_RESULT_REVALIDATE, getHttpStatus, handleStaticPropsError } from '@/shared/libs/isr'
import { PremadeStoryProductPage } from '@/slices/Shop/PremadeStoryDetail'

const REVALIDATION_FREQUENCY = 60 * 10 // 10 minutes

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const stories = await storefrontPremadeStoriesList({ language_code: 'en' })

    const paths = stories.map(story => ({
      params: { slug: story.slug },
    }))

    return {
      paths,
      fallback: 'blocking',
    }
  } catch (error) {
    console.error('Error pre-rendering premade story paths:', error)

    return {
      paths: [],
      fallback: 'blocking',
    }
  }
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const storySlug = context.params?.slug?.toString()

  if (!storySlug) {
    return { notFound: true, revalidate: EMPTY_RESULT_REVALIDATE }
  }

  let story: PremadeStoryDetail

  try {
    story = await storefrontPremadeStoriesRetrieve(storySlug)
  } catch (error) {
    // A real 404 is a (short-lived) not found; anything else rethrows during
    // revalidation so Next keeps the last good page. Only `next build` swallows it.
    if (getHttpStatus(error) === 404) {
      return { notFound: true, revalidate: EMPTY_RESULT_REVALIDATE }
    }

    return handleStaticPropsError(error, { notFound: true as const, revalidate: EMPTY_RESULT_REVALIDATE }, `premade story ${storySlug}`)
  }

  if (!story.supports_photo_upload) {
    return { notFound: true, revalidate: EMPTY_RESULT_REVALIDATE }
  }

  return withLayoutProps(context, {
    props: {
      story,
    },
    revalidate: REVALIDATION_FREQUENCY,
  })
}

export type PremadeStoryDetailPageProps = GlobalProps & {
  story: PremadeStoryDetail
}

const PremadeStoryDetailPage: NextPage<PremadeStoryDetailPageProps> = ({
  story,
  ...layoutProps
}) => {
  return (
    <Layout {...layoutProps}>
      <BaseSeo
        title={story.title}
        description={story.description}
        image={story.thumbnail_url}
      />
      <PremadeStoryProductPage story={story} />
    </Layout>
  )
}

export default PremadeStoryDetailPage
